import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, RefreshCw } from 'lucide-react';
import {
  fetchTranscriptsByParticipantEmail,
  formatTranscriptWhen,
  transcriptChunksStatusLabel,
  transcriptPreview,
  type LinkedTranscriptRow,
} from '../../lib/transcriptsByEmail';

export default function SubscriberLinkedTranscriptsSection({ email }: { email: string }) {
  const [rows, setRows] = useState<LinkedTranscriptRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const trimmed = email.trim();
    if (!trimmed) {
      setRows([]);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      setRows(await fetchTranscriptsByParticipantEmail(trimmed));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load transcripts');
    } finally {
      setLoading(false);
    }
  }, [email]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col min-w-0">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-violet-50/90 dark:bg-violet-950/30 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <FileText className="w-5 h-5 text-violet-700 dark:text-violet-400 shrink-0 mt-0.5" strokeWidth={1.75} />
            <div>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
                Transcripts
              </h2>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 normal-case tracking-normal">
                Meetings where participants include{' '}
                <span className="font-medium text-gray-700 dark:text-gray-300 break-all">{email.trim()}</span>.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading || !email.trim()}
            className="inline-flex shrink-0 items-center justify-center p-2 rounded-lg border border-violet-200 dark:border-violet-800 text-violet-800 dark:text-violet-200 bg-white/80 dark:bg-gray-800 hover:bg-violet-100/80 dark:hover:bg-violet-950/50 disabled:opacity-50"
            aria-label="Refresh transcripts"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {error ? (
          <div className="px-6 py-8 flex flex-col items-center justify-center gap-3 text-center">
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            <button
              type="button"
              onClick={() => void load()}
              className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-600 hover:bg-red-700 text-white"
            >
              Try again
            </button>
          </div>
        ) : loading && rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-2 text-gray-500 dark:text-gray-400">
            <RefreshCw className="w-7 h-7 animate-spin text-violet-500" />
            <p className="text-sm">Loading transcripts…</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700 flex-1 m-0 p-0 list-none">
            {rows.length === 0 ? (
              <li className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                No transcripts list this email in participants.
              </li>
            ) : (
              rows.map((row) => (
                <li key={row.id} className="border-0 p-0">
                  <Link
                    to={`/transcripts/${row.id}`}
                    className="block w-full px-6 py-4 text-left hover:bg-gray-50/80 dark:hover:bg-gray-700/30 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-inset"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <p className="text-sm font-medium text-gray-900 dark:text-white min-w-0">{row.title}</p>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          Search index
                        </span>
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                            row.transcript_chunks_status === 'ready'
                              ? 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200'
                              : row.transcript_chunks_status === 'pending'
                                ? 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100'
                                : row.transcript_chunks_status === 'error'
                                  ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
                                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                          }`}
                        >
                          {transcriptChunksStatusLabel(row.transcript_chunks_status)}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {formatTranscriptWhen(row.meeting_date)}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 leading-snug line-clamp-2">
                      {transcriptPreview(row)}
                    </p>
                  </Link>
                </li>
              ))
            )}
          </ul>
        )}
    </section>
  );
}
